import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Category, CategoryService, Contact, ContactListModalComponent, FirebaseService, GoogleMapsComponent, GooglePlaceType, Response, ResponseService, UploadTypeEnum, UtilHelper, Pager, Channel, ChannelService } from 'lib-trend-core';
import { BehaviorSubject, debounceTime, Observable } from 'rxjs';
import { CategoryAddComponent } from '../../category/category-add/category-add.component';
import { ButtonActionsTypeEnum } from '@trendbuild/trend-z-api';

@Component({
    selector: 'app-response-edit-component',
    // templateUrl: './response-edit.component.html',
    templateUrl: './response-edit.v2.component.html',
    // styleUrl: 'response-edit.component.scss'
    styleUrl: 'response-edit.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResponseEditComponent extends AbstractComponent {
  filterControlCategory: FormControl = new FormControl('');

  response: Response = {} as Response;

  listCategory: Array<Category> = new Array<Category>();
  allCategories: Array<Category> = new Array<Category>();

  responseTypes = [
    { value: 'TEXT', label: 'response.type.text' },
    { value: 'CONTACT', label: 'response.type.contact' },
    { value: 'LOCATION', label: 'response.type.location' },
    { value: 'BUTTONS', label: 'response.type.buttons' }
  ];

  pager: Pager<Channel> = new Pager<Channel>({ perPage: 9 });
  listObservable: Observable<Pager<Channel>>;


  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<ResponseEditComponent>,
    public responseService: ResponseService,
    private categoryService: CategoryService,
    private dialogCategory: MatDialog,
    private firebaseService: FirebaseService,
    private channelService: ChannelService,
    private modalSendContact: MatDialog,
    private modalSendLocation: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: Response
  ) {
    super(injector);
    this.response = {
      type: data.type
    } as Response;
    this.createForm();
    this.getListCategory();
    this.fetchFilterCategory();
  }

  ngOnInit() {
    this.isNew = !this.data || !this.data._id;

    if (!this.isNew) {
      this.response = this.data;
      this.response.type = this.data.type;
      this.response.category = this.data.category;
      this.formGroup.patchValue({ ...this.data, category: this.data.category._id });
      if (this.formGroup.get('typeResponse').value !== 'BUTTONS') {
        this.formGroup.get('buttonActions').setValue([]);
      }
      if (this.data.vcardContact) {
        this.formGroup.get('typeResponse').setValue('CONTACT');
      } else if (this.data.place) {
        this.formGroup.get('typeResponse').setValue('LOCATION');
      } else if (this.data.buttonActions?.length > 0) {
        this.formGroup.get('typeResponse').setValue('BUTTONS');
        const buttonActionsArray = Array.isArray(this.data.buttonActions) ? this.data.buttonActions : [this.data.buttonActions];
        this.initButtonActions(buttonActionsArray);
      }
    } else {
      this.response.type = this.data.type;
      this.formGroup.patchValue({ type: this.data.type });
    }
    this.formGroup.controls['typeResponse'].valueChanges.subscribe(value => {
      if (value === 'CONTACT') {
        this.formGroup.get('vcardContact').setValidators([Validators.required]);
        this.formGroup.get('place').clearValidators();
        this.clearButtonFormArray();
      } else if (value === 'LOCATION') {
        this.formGroup.get('vcardContact').clearValidators();
        this.formGroup.get('place').setValidators([Validators.required]);
        this.clearButtonFormArray();
      } else if (value === 'BUTTONS') {
        this.formGroup.get('vcardContact').clearValidators();
        this.formGroup.get('place').clearValidators();
        if (this.getButtonActionsFormArray().length === 0) {
          this.addButtonAction();
        }
      } else {
        this.formGroup.get('vcardContact').clearValidators();
        this.formGroup.get('place').clearValidators();
        this.clearButtonFormArray();
      }

      this.formGroup.valueChanges.subscribe(value => {
        Object.assign(this.response, value);
      });
      this.formGroup.get('vcardContact').setValue(null);
      this.formGroup.get('message').setValue(null);
      this.formGroup.get('place').setValue(null);
      this.formGroup.get('vcardContact').updateValueAndValidity();
      this.formGroup.get('message').updateValueAndValidity();
      this.formGroup.get('place').updateValueAndValidity();
    });

    this.getList();
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      name: [null, [Validators.required, Validators.maxLength(100)]],
      category: [null, [Validators.required]],
      message: [null, [Validators.required]],
      type: [this.data.type, Validators.required],
      typeResponse: ['TEXT'],
      vcardContact: [null],
      place: [null],
      title: [null],
      footer: [null],
      buttonActions: this.formBuilder.array([])
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  save() {
    this.loading = true;
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup)
      return;
    }

    this.response = { ...this.response, ...this.formGroup.value };

    if (this.isNew) {
      this.responseService.create(this.response).subscribe({
        next: (value) => {
          this.dialogRef.close(value);
          this.alertService.success(this.t('response.edit.toastCreateSuccess'));
        },
        error: (err) => {
          this.loading = false;
          this.alertService.error(err.error.message);
        },
        complete: () => this.loading = false,
      });
    } else {
      this.responseService.update(this.response._id, this.response).subscribe({
        next: (value) => {
          this.dialogRef.close(value);
          this.alertService.success(this.t('response.edit.toastUpdateSuccess'))
        },
        error: (err) => {
          this.loading = false;
          this.alertService.error(err.error.message);
        },
        complete: () => this.loading = false,
      });
    }
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          this.loadingSpinnerSubject.next(true);
          const filename = file.name + '_' + new Date().getTime();
          this.firebaseService.uploadFile(file, filename, UploadTypeEnum.RESPONSE).then((snapshot: { url: string }) => {
            this.loading = false;
            this.response.midia = snapshot.url;
            this.response.contentType = file.type;
            this.loadingSpinnerSubject.next(false);
          });
        } catch (error) {
          this.alertService.error(this.t('response.edit.toastUploadError'));
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  addCategory(): void {
    const dialogRefCategory = this.dialogCategory.open(CategoryAddComponent, {
      width: '600px',
    });

    dialogRefCategory.afterClosed().subscribe(result => {
      this.fetchFilterCategory();
    });
  }

  midiaImage(): string {
    if (this.response.midia) {
      const mediaType = this.helperExtension(this.response.midia);

      switch (mediaType) {
        case 'image':
          return this.response.midia;
        case 'audio':
          return UtilHelper.on_audio;
        case 'video':
          return UtilHelper.on_video;
        case 'document':
          return UtilHelper.on_file;
        default:
          return UtilHelper.default_image;
      }
    } else {
      return UtilHelper.no_image;
    }
  }

  removeMidia(): void {
    if (this.response.midia) {
      this.firebaseService.deleteFile(this.response.midia)
        .then(() => {
          this.resetFileState();
        })
        .catch(error => {
          this.alertService.error(this.t('response.edit.toastDeleteFileError'));
          this.resetFileState();
        });
    }
  }

  private helperExtension(midia: string): 'image' | 'audio' | 'video' | 'document' | 'text' {
    const decodedUrl = decodeURIComponent(midia);

    const fileNameWithQuery = decodedUrl.split('/').pop() || '';
    const fileName = fileNameWithQuery.split('?')[0];

    const extension = fileName.split('.').pop()?.toLowerCase();

    const regex = /_(\d+)/;
    const nameWithoutPart = extension.replace(regex, '');

    switch (nameWithoutPart.trim()) {
      case 'mp4':
        return 'video';
      case 'pdf':
        return 'document';
      case 'mp3':
        return 'audio';
      case 'png':
        return 'image';
      case 'jpg':
        return 'image';
      case 'jpeg':
        return 'image'
      default:
        return 'text';
    }
  }

  private resetFileState(): void {
    this.response.midia = '';
  }

  private getListCategory(): void {
    this.categoryService.getList().subscribe({
      next: value => {
        this.allCategories = value;
        this.listCategory = value;
      },
      error: err => this.alertService.error(this.t('response.edit.toastFetchCategoriesError')),
    });
  }

  private fetchFilterCategory(): void {
    this.filterControlCategory.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('category')?.value || null;

      this.listCategory = this.allCategories.filter(category =>
        category.name.toLowerCase().includes(value.toLowerCase()) &&
        category._id !== currentValue
      );

      const existingCategory = this.allCategories.find(category => category._id === currentValue);
      if (existingCategory) {
        this.listCategory.push(existingCategory);
      }
    });
  }

  onFocusCategory(): void {
    this.listCategory = this.allCategories;
  }

  isTypeText(): boolean {
    return this.formGroup.get('typeResponse')?.value === 'TEXT';
  }

  isTypeContact(): boolean {
    return this.formGroup.get('typeResponse')?.value === 'CONTACT';
  }

  isTypeLocation(): boolean {
    return this.formGroup.get('typeResponse')?.value === 'LOCATION';
  }

  isTypeButton(): boolean {
    return this.formGroup.get('typeResponse')?.value === 'BUTTONS';
  }

  selectContact(): void {
    const dialogRef = this.modalSendContact.open(ContactListModalComponent, {
      width: '1000px',
    });
    dialogRef.afterClosed().subscribe((contact: Contact) => {
      if (!!contact) {
        this.formGroup.get('vcardContact').setValue(contact);
        this.formGroup.get('message').setValue(this.t('response.edit.contactMessage', { name: contact.name, phone: contact.phone }));
      }
    });
  }

  selectLocation(): void {
    const dialogRef = this.modalSendLocation.open(GoogleMapsComponent, {
      width: '1000px',
    });
    dialogRef.afterClosed().subscribe((place: GooglePlaceType) => {
      if (!!place) {
        this.formGroup.get('place').setValue(place);
        this.formGroup.get('message').setValue(this.t('response.edit.locationMessage', { address: place.formattedAddress, longitude: place.location.longitude, latitude: place.location.latitude }));
      }
    });
  }

  getContact() {
    return this.formGroup.get('vcardContact').value;
  }

  getPlace() {
    return this.formGroup.get('place').value;
  }

  removeContact(): void {
    this.formGroup.get('vcardContact').setValue(null);
  }

  removePlace(): void {
    this.formGroup.get('place').setValue(null);
  }

  setVariableInContent(value: string): void {
    this.formGroup.patchValue({ message: this.formGroup.get('message')?.value + " " + value });
  }

  getButtonActionsFormArray() {
    return this.formGroup.get('buttonActions') as any;
  }

  addButtonAction() {
    const buttonGroup = this.formBuilder.group({
      type: [ButtonActionsTypeEnum.reply, Validators.required],
      label: ['', Validators.required],
      phone: [''],
      url: ['']
    });

    buttonGroup.get('type').valueChanges.subscribe(type => {
      if (type === ButtonActionsTypeEnum.call) {
        buttonGroup.get('phone').setValidators([Validators.required]);
        buttonGroup.get('url').clearValidators();
      } else if (type === ButtonActionsTypeEnum.url) {
        buttonGroup.get('url').setValidators([Validators.required]);
        buttonGroup.get('phone').clearValidators();
      } else {
        buttonGroup.get('phone').clearValidators();
        buttonGroup.get('url').clearValidators();
      }
      buttonGroup.get('phone').updateValueAndValidity();
      buttonGroup.get('url').updateValueAndValidity();
    });

    this.getButtonActionsFormArray().push(buttonGroup);
  }

  removeButtonAction(index: number) {
    this.getButtonActionsFormArray().removeAt(index);
  }

  clearButtonFormArray() {
    while (this.getButtonActionsFormArray().length) {
      this.getButtonActionsFormArray().removeAt(0);
    }
  }

  initButtonActions(buttons: any[]) {
    this.clearButtonFormArray();
    if (buttons && buttons.length) {
      buttons.forEach(button => {
        const buttonGroup = this.formBuilder.group({
          type: [button.type, Validators.required],
          label: [button.label, Validators.required],
          phone: [button.phone],
          url: [button.url]
        });

        buttonGroup.get('type').valueChanges.subscribe(type => {
          if (type === ButtonActionsTypeEnum.call) {
            buttonGroup.get('phone').setValidators([Validators.required]);
            buttonGroup.get('url').clearValidators();
          } else if (type === ButtonActionsTypeEnum.url) {
            buttonGroup.get('url').setValidators([Validators.required]);
            buttonGroup.get('phone').clearValidators();
          } else {
            buttonGroup.get('phone').clearValidators();
            buttonGroup.get('url').clearValidators();
          }
          buttonGroup.get('phone').updateValueAndValidity();
          buttonGroup.get('url').updateValueAndValidity();
        });

        this.getButtonActionsFormArray().push(buttonGroup);
      });
    }
  }

  getList() {
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    }

    this.channelService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams).subscribe({
      next: (pager: Pager<Channel>) => {
        this.pager = pager;
      },
      error: (err) => this.alertService.error(err.error.message)
    })
  }

}
